/**
 * Receives a string that is expected to be following the semantic versioning (SemVer)
 * system for defining version nomenclature, that is, X.Y.Z-foo+bar, where:
 *
 * X: major
 * Y: minor
 * Z: patch
 * foo: pre-release label (optional)
 * bar: build metadata (optional)
 *
 * From that, returns every component but now separated.
 *
 * @param version string that represents a version (expected to be following SemVer).
 * @returns components from SemVer version.
 */

type ParseVersionFollowingSemVerReturnValue = {
  major: string
  minor: string
  patch: string
  preReleaseLabel: string
  buildMetadata: string
}

export const parseVersionFollowingSemVer = (version: string): ParseVersionFollowingSemVerReturnValue => {
  const numPattern = '0|(?:[1-9]\\d*)'
  const extraDataPattern = '(?:0|(?:[1-9A-Za-z-][0-9A-Za-z-]*))(?:\\.(?:0|(?:[1-9A-Za-z-][0-9A-Za-z-]*)))*)'
  const finalPattern = `^(?<MAJOR>${numPattern})\\.(?<MINOR>${numPattern})\\.(?<PATCH>${numPattern})(?:-(?<prerelease>${extraDataPattern})?(?:\\+(?<build>${extraDataPattern})?$`

  const regex = new RegExp(finalPattern)

  const match = regex.exec(version)

  if (!match) {
    throw new Error(`${version} does not match semantic versioning pattern.`)
  }

  /* First value from match, which is the full version, is discarded. */
  const [, major, minor, patch, preReleaseLabel, buildMetadata] = match

  return { major, minor, patch, preReleaseLabel, buildMetadata }
}
